import { useCallback, useEffect, useRef, useState } from "react";
import { transcribeAudio } from "@/api/ai.api";
import { downsampleBuffer, floatTo16BitPCM, pcmToWav, PCM_SAMPLE_RATE } from "@/lib/audio-pcm";

/**
 * Voice input for the copilot chat: records the question, then transcribes it
 * through the backend's Azure Speech (BAA-covered). The browser's built-in
 * SpeechRecognition is never used — Chrome/Edge send that audio to consumer
 * cloud services.
 */
export function useCopilotDictation(onTranscript: (text: string) => void) {
  const [listening, setListening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const activeRef = useRef(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const onTranscriptRef = useRef(onTranscript);
  onTranscriptRef.current = onTranscript;

  const stopAzureCapture = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    mediaRecorderRef.current = null;
  }, []);

  const stop = useCallback(() => {
    activeRef.current = false;
    setListening(false);
    stopAzureCapture();
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    mediaStreamRef.current = null;
  }, [stopAzureCapture]);

  const transcribeBlobAzure = useCallback(async (blob: Blob) => {
    try {
      const arrayBuffer = await blob.arrayBuffer();
      const audioContext = new AudioContext();
      const decoded = await audioContext.decodeAudioData(arrayBuffer.slice(0));
      await audioContext.close();

      const channel = decoded.getChannelData(0);
      const downsampled = downsampleBuffer(channel, decoded.sampleRate, PCM_SAMPLE_RATE);
      const pcm = floatTo16BitPCM(downsampled);
      const wav = pcmToWav(pcm);

      const result = await transcribeAudio(wav);
      const text = result.text?.trim();
      if (text) onTranscriptRef.current(text);
    } catch {
      setError("Could not transcribe audio. Try again or type your question.");
    }
  }, []);

  const startAzureCapture = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaStreamRef.current = stream;
    const recorder = new MediaRecorder(stream);
    chunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) chunksRef.current.push(e.data);
    };

    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
      chunksRef.current = [];
      stream.getTracks().forEach((t) => t.stop());
      mediaStreamRef.current = null;
      if (blob.size > 0) {
        await transcribeBlobAzure(blob);
      }
    };

    recorder.start();
    mediaRecorderRef.current = recorder;
  }, [transcribeBlobAzure]);

  const toggle = useCallback(async () => {
    setError(null);
    if (listening) {
      stop();
      return;
    }

    activeRef.current = true;
    setListening(true);

    try {
      await startAzureCapture();
    } catch {
      activeRef.current = false;
      setListening(false);
      setError("Microphone unavailable. Check permissions and try again.");
    }
  }, [listening, startAzureCapture, stop]);

  useEffect(() => () => stop(), [stop]);

  return {
    listening,
    error,
    toggle,
    stop,
    supported: typeof navigator !== "undefined" && !!navigator.mediaDevices?.getUserMedia,
  };
}
